import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAppState, Task, Project, CalendarEvent, AI_PERSONAS } from './AppContext';
import { GeminiLiveSession, AppContext as AIContext } from '../services/geminiLiveService';
import { AudioCapture, AudioPlayer, VoiceActivityDetector } from '../utils/audioUtils';
import { useAuth } from './AuthContext';
import { useFocusContext } from './FocusContext';
import {
    Conversation,
    loadConversations,
    saveConversation,
    createNewConversation,
} from '../services/chatHistoryService';
import {
    saveDebriefFacts,
    searchSemanticMemories,
    VectorMemoryEntry,
} from '../services/secondBrainService';
import {
    calculateWorkloadScore,
    getHabitGaps,
    getOverdueHighPriorityTasks,
    findAvailableSlots,
} from '../utils/workloadUtils';
import { getUserIdentity, saveUserIdentity, addRolesToIdentity, addVipsToIdentity } from '../services/trainingService';

type AssistantState = 'idle' | 'connecting' | 'ready' | 'listening' | 'processing' | 'speaking' | 'error';

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    actions?: string[];
}

// Change log entry for tracking all modifications
interface ChangeLogEntry {
    timestamp: number;
    action: string;
    itemType: 'task' | 'project' | 'event' | 'subtask';
    itemId: string;
    itemTitle: string;
    details?: string;
}

// Enhanced session memory with full change tracking
interface SessionMemory {
    // Maps for quick lookup: title -> {id, title, createdAt}
    tasks: Map<string, { id: string; title: string; createdAt: number }>;
    projects: Map<string, { id: string; name: string; createdAt: number }>;
    events: Map<string, { id: string; title: string; createdAt: number }>;

    // Last mentioned items for pronoun resolution
    lastMentionedTask: string | null;
    lastMentionedProject: string | null;
    lastMentionedEvent: string | null;
    lastMentionedSubtask: { taskId: string; subtaskId: string; title: string } | null;

    // Full change log for AI context
    changeLog: ChangeLogEntry[];
}

interface VoiceAssistantContextType {
    isOpen: boolean;
    isMinimized: boolean;
    state: AssistantState;
    chatHistory: ChatMessage[];
    errorMessage: string;
    open: () => void;
    close: () => void;
    minimize: () => void;
    expand: () => void;
    startListening: () => Promise<void>;
    stopListening: () => void;
    isRecording: boolean;
    volumeLevel: number; // 0-1
}

const VoiceAssistantContext = createContext<VoiceAssistantContextType | null>(null);

export function useVoiceAssistant() {
    const context = useContext(VoiceAssistantContext);
    if (!context) {
        throw new Error('useVoiceAssistant must be used within VoiceAssistantProvider');
    }
    return context;
}

// Enhanced fuzzy find that checks session memory first and handles pronouns
function fuzzyFindTask(tasks: Task[], searchTitle: string, memory: SessionMemory): Task | undefined {
    // ... (rest of function omitted for brevity, assuming strict context)
    const search = searchTitle.toLowerCase().trim();

    // Handle pronouns - use last mentioned
    if (['it', 'that', 'the task', 'this task', 'that task', 'this', 'that one'].includes(search)) {
        if (memory.lastMentionedTask) {
            const task = tasks.find(t => t.id === memory.lastMentionedTask);
            if (task) return task;
        }
    }

    // Check session memory first
    for (const [key, item] of memory.tasks) {
        if (key.toLowerCase() === search ||
            key.toLowerCase().includes(search) ||
            search.includes(key.toLowerCase())) {
            const task = tasks.find(t => t.id === item.id);
            if (task) return task;
        }
    }

    // ... (rest of logic)

    // Priority 1: Exact match
    let found = tasks.find(t => t.title.toLowerCase() === search);
    if (found) return found;

    // Priority 2: Title contains search
    found = tasks.find(t => t.title.toLowerCase().includes(search));
    if (found) return found;

    // Priority 3: Search contains title
    found = tasks.find(t => search.includes(t.title.toLowerCase()));
    if (found) return found;

    // Priority 4: Word overlap
    const searchWords = search.split(/\s+/).filter(w => w.length > 2);
    found = tasks.find(t => {
        const titleWords = t.title.toLowerCase().split(/\s+/);
        return searchWords.some(sw => titleWords.some(tw => tw.includes(sw) || sw.includes(tw)));
    });

    return found;
}

function fuzzyFindProject(projects: Project[], searchName: string, memory: SessionMemory): Project | undefined {
    const search = searchName.toLowerCase().trim();

    if (['it', 'that', 'the project', 'this project', 'that project'].includes(search)) {
        if (memory.lastMentionedProject) {
            return projects.find(p => p.id === memory.lastMentionedProject);
        }
    }

    // Check session memory first
    for (const [key, item] of memory.projects) {
        if (key.toLowerCase() === search ||
            key.toLowerCase().includes(search) ||
            search.includes(key.toLowerCase())) {
            const project = projects.find(p => p.id === item.id);
            if (project) return project;
        }
    }

    // Exact match
    let found = projects.find(p => p.name.toLowerCase() === search);
    if (found) return found;

    // Contains
    return projects.find(p =>
        p.name.toLowerCase().includes(search) ||
        search.includes(p.name.toLowerCase())
    );
}

function fuzzyFindEvent(events: CalendarEvent[], searchTitle: string, memory: SessionMemory): CalendarEvent | undefined {
    const search = searchTitle.toLowerCase().trim();

    if (['it', 'that', 'the event', 'this event', 'the meeting', 'that meeting'].includes(search)) {
        if (memory.lastMentionedEvent) {
            return events.find(e => e.id === memory.lastMentionedEvent);
        }
    }

    // Check session memory first
    for (const [key, item] of memory.events) {
        if (key.toLowerCase() === search ||
            key.toLowerCase().includes(search) ||
            search.includes(key.toLowerCase())) {
            const event = events.find(e => e.id === item.id);
            if (event) return event;
        }
    }

    // Exact match
    let found = events.find(e => e.title.toLowerCase() === search);
    if (found) return found;

    // Contains
    return events.find(e =>
        e.title.toLowerCase().includes(search) ||
        search.includes(e.title.toLowerCase())
    );
}

// Find subtask with fuzzy matching
function fuzzyFindSubtask(task: Task, searchTitle: string, memory: SessionMemory): { id: string; title: string; completed: boolean } | undefined {
    const search = searchTitle.toLowerCase().trim();

    // Handle pronouns for subtasks
    if (['it', 'that', 'the subtask', 'this subtask', 'that subtask', 'that one'].includes(search)) {
        if (memory.lastMentionedSubtask && memory.lastMentionedSubtask.taskId === task.id) {
            return task.subtasks?.find(s => s.id === memory.lastMentionedSubtask!.subtaskId);
        }
    }

    if (!task.subtasks) return undefined;

    // Exact match
    let found = task.subtasks.find(s => s.title.toLowerCase() === search);
    if (found) return found;

    // Contains
    found = task.subtasks.find(s =>
        s.title.toLowerCase().includes(search) ||
        search.includes(s.title.toLowerCase())
    );

    return found;
}// ... 

export function VoiceAssistantProvider({ children }: { children: React.ReactNode }) {
    const appState = useAppState();
    const { state: focusState } = useFocusContext();
    const { user } = useAuth();

    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [state, setState] = useState<AssistantState>('idle');
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(0);

    // Conversation persistence state
    const [, setConversations] = useState<Conversation[]>([]);
    const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
    const conversationLoadedRef = useRef(false);


    const sessionRef = useRef<GeminiLiveSession | null>(null);
    const captureRef = useRef<AudioCapture | null>(null);
    const playerRef = useRef<AudioPlayer | null>(null);
    const vadRef = useRef<VoiceActivityDetector | null>(null);
    const reconnectAttemptedRef = useRef(false);

    // Keep appState in a ref so it's always current in callbacks
    const appStateRef = useRef(appState);
    useEffect(() => {
        appStateRef.current = appState;
    }, [appState]);

    const isFocusModeActiveRef = useRef(focusState.isFocusModeActive);
    useEffect(() => {
        isFocusModeActiveRef.current = focusState.isFocusModeActive;
    }, [focusState.isFocusModeActive]);

    // Load conversations on mount
    useEffect(() => {
        if (user?.id && !conversationLoadedRef.current) {
            conversationLoadedRef.current = true;
            loadConversations(user.id).then(convs => {
                setConversations(convs);
                // Resume last conversation or create new one
                if (convs.length > 0) {
                    const lastConv = convs[0];
                    setCurrentConversation(lastConv);
                    // Convert stored messages to chat format
                    const chatMsgs: ChatMessage[] = lastConv.messages.map(m => ({
                        role: m.role,
                        text: m.content,
                    }));
                    setChatHistory(chatMsgs);
                    console.log('[ChatHistory] Loaded conversation:', lastConv.id, 'with', chatMsgs.length, 'messages');
                }
            });
        }
    }, [user?.id]);

    // Save conversation when chat history changes
    // Use ref to access currentConversation without adding it to deps (prevents infinite loop)
    const currentConversationRef = useRef(currentConversation);
    currentConversationRef.current = currentConversation;

    useEffect(() => {
        if (!user?.id || chatHistory.length === 0) return;

        // Convert chat messages to storage format
        const messages = chatHistory.map(m => ({
            role: m.role,
            content: m.text,
            timestamp: new Date().toISOString(),
        }));

        // Update or create conversation
        let conv = currentConversationRef.current;
        if (!conv) {
            conv = createNewConversation();
        }

        const updatedConv = { ...conv, messages, updatedAt: new Date().toISOString() };
        saveConversation(user.id, updatedConv);
        setCurrentConversation(updatedConv);
    }, [chatHistory, user?.id]); // Removed currentConversation from deps to prevent infinite loop

    // Session memory for tracking ALL changes during this session
    const sessionMemoryRef = useRef<SessionMemory>({
        tasks: new Map(),
        projects: new Map(),
        events: new Map(),
        lastMentionedTask: null,
        lastMentionedProject: null,
        lastMentionedEvent: null,
        lastMentionedSubtask: null,
        changeLog: [],
    });

    // Track current evening debrief session (persisted to localStorage for cross-navigation)
    interface DebriefSession {
        date: string;
        rating: number;
        wins: string[];
        improvements: string[];
        gratitude: string[];
        journal: string;
        isActive: boolean;
    }

    const DEBRIEF_SESSION_KEY = 'way2will_active_debrief';

    const getDebriefSession = (): DebriefSession => {
        try {
            const stored = localStorage.getItem(DEBRIEF_SESSION_KEY);
            console.log('[VoiceAssistant] getDebriefSession - raw value:', stored);
            if (stored) {
                const parsed = JSON.parse(stored);
                console.log('[VoiceAssistant] getDebriefSession - parsed:', parsed);
                return parsed;
            }
        } catch (e) {
            console.error('[VoiceAssistant] Error reading debrief session:', e);
        }
        console.log('[VoiceAssistant] getDebriefSession - returning default (no session found)');
        return {
            date: new Date().toISOString().split('T')[0],
            rating: 0,
            wins: [],
            improvements: [],
            gratitude: [],
            journal: '',
            isActive: false,
        };
    };

    const setDebriefSession = (session: DebriefSession) => {
        console.log('[VoiceAssistant] setDebriefSession - saving:', session);
        localStorage.setItem(DEBRIEF_SESSION_KEY, JSON.stringify(session));
        console.log('[VoiceAssistant] setDebriefSession - verified:', localStorage.getItem(DEBRIEF_SESSION_KEY));
    };

    // Log a change to session memory
    const logChange = useCallback((
        action: string,
        itemType: 'task' | 'project' | 'event' | 'subtask',
        itemId: string,
        itemTitle: string,
        details?: string
    ) => {
        const entry: ChangeLogEntry = {
            timestamp: Date.now(),
            action,
            itemType,
            itemId,
            itemTitle,
            details,
        };
        sessionMemoryRef.current.changeLog.push(entry);
        // Keep only last 20 changes to avoid memory bloat
        if (sessionMemoryRef.current.changeLog.length > 20) {
            sessionMemoryRef.current.changeLog.shift();
        }
        console.log('[SessionMemory] Change logged:', entry);
    }, []);

    // Add action to last assistant message
    const addAction = useCallback((actionText: string) => {
        setChatHistory(prev => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant') {
                    updated[i] = {
                        ...updated[i],
                        actions: [...(updated[i].actions || []), actionText],
                    };
                    break;
                }
            }
            return updated;
        });
    }, []);

    // Execute function calls from AI - Full CRUD with bulletproof session memory
    const executeFunction = useCallback(async (call: any): Promise<string> => {
        const name = call.name;
        const args = call.args || {};
        const memory = sessionMemoryRef.current;
        // CRITICAL: Get fresh appState from ref, not the stale closure!
        const currentAppState = appStateRef.current;

        console.log('[VoiceAssistant] Executing:', name, args);
        console.log('[VoiceAssistant] Session memory state:', {
            tasks: Array.from(memory.tasks.keys()),
            projects: Array.from(memory.projects.keys()),
            lastTask: memory.lastMentionedTask,
            changeLogSize: memory.changeLog.length,
        });

        try {
            const today = new Date().toISOString().split('T')[0];

            switch (name) {
                // ============ TASKS ============
                case 'createTask': {
                    // CRITICAL: Verify appState methods exist
                    if (!currentAppState.addTask) {
                        console.error('[VoiceAssistant] CRITICAL: addTask method not found on appStateRef!');
                        console.error('[VoiceAssistant] Available keys:', Object.keys(currentAppState));
                        return 'Error: Task system not initialized';
                    }

                    let projectId: string | undefined;
                    if (args.projectName) {
                        const project = fuzzyFindProject(currentAppState.projects, args.projectName, memory);
                        if (project) {
                            projectId = project.id;
                            memory.lastMentionedProject = project.id;
                        }
                    }

                    console.log('[VoiceAssistant] About to call addTask with:', {
                        title: args.title,
                        priority: args.priority || 'medium',
                        dueDate: args.dueDate || today,
                        projectId,
                    });

                    try {
                        currentAppState.addTask({
                            title: args.title,
                            completed: false,
                            priority: args.priority || 'medium',
                            dueDate: args.dueDate || today,
                            timeStart: args.timeStart,
                            timeEnd: args.timeEnd,
                            projectId,
                            context: 'Voice',
                            contextColor: '#0D5F6B',
                            contextBgColor: '#D9EEF2',
                            subtasks: [],
                        });
                        console.log('[VoiceAssistant] addTask called successfully');
                    } catch (taskError) {
                        console.error('[VoiceAssistant] addTask threw error:', taskError);
                        return `Error creating task: ${taskError}`;
                    }

                    // IMMEDIATELY update session memory with a placeholder ID
                    // We'll update it once we can find the actual task
                    const tempId = `temp_${Date.now()}`;
                    memory.tasks.set(args.title, { id: tempId, title: args.title, createdAt: Date.now() });
                    memory.lastMentionedTask = tempId;

                    // Update with real ID after React state updates
                    setTimeout(() => {
                        const freshState = appStateRef.current;
                        const createdTask = freshState.tasks.find(t => t.title === args.title);
                        if (createdTask) {
                            memory.tasks.set(args.title, { id: createdTask.id, title: args.title, createdAt: Date.now() });
                            memory.lastMentionedTask = createdTask.id;
                            console.log('[SessionMemory] Updated task ID:', createdTask.id, 'for:', args.title);
                        }
                    }, 100);

                    logChange('created', 'task', tempId, args.title, projectId ? `in project ${args.projectName}` : undefined);
                    addAction(`✓ Created task "${args.title}"`);
                    return `Created task "${args.title}". I've noted this - you can refer to it as "the task" or "${args.title}".`;
                }

                case 'listTasks': {
                    let tasks = currentAppState.tasks.filter(t => args.showCompleted ? true : !t.completed);

                    if (args.projectName) {
                        const project = fuzzyFindProject(currentAppState.projects, args.projectName, memory);
                        if (project) {
                            tasks = tasks.filter(t => t.projectId === project.id);
                            memory.lastMentionedProject = project.id;
                        }
                    }

                    if (tasks.length === 0) return 'No tasks found';

                    const taskList = tasks.slice(0, 10).map(t =>
                        `"${t.title}" [${t.priority}]${t.dueDate ? ` due ${t.dueDate}` : ''}${t.subtasks?.length ? ` (${t.subtasks.length} subtasks)` : ''}`
                    ).join('; ');

                    return `Tasks: ${taskList}`;
                }

                case 'updateTask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}". Try saying the exact name or "the task" if referring to one you just mentioned.`;

                    const updates: any = {};
                    if (args.newTitle) updates.title = args.newTitle;
                    if (args.priority) updates.priority = args.priority;
                    if (args.dueDate) updates.dueDate = args.dueDate;
                    if (args.timeStart) updates.timeStart = args.timeStart;
                    if (args.timeEnd) updates.timeEnd = args.timeEnd;

                    // CRITICAL: Verify updateTask method exists
                    if (!currentAppState.updateTask) {
                        console.error('[VoiceAssistant] CRITICAL: updateTask method not found on appStateRef!');
                        return 'Error: Task system not initialized';
                    }

                    console.log('[VoiceAssistant] About to call updateTask with:', {
                        taskId: task.id,
                        updates,
                    });

                    try {
                        currentAppState.updateTask(task.id, updates);
                        console.log('[VoiceAssistant] updateTask called successfully');
                    } catch (updateError) {
                        console.error('[VoiceAssistant] updateTask threw error:', updateError);
                        return `Error updating task: ${updateError}`;
                    }

                    memory.lastMentionedTask = task.id;

                    // Update session memory if title changed
                    if (args.newTitle) {
                        memory.tasks.delete(task.title);
                        memory.tasks.set(args.newTitle, { id: task.id, title: args.newTitle, createdAt: Date.now() });
                        logChange('renamed', 'task', task.id, args.newTitle, `from "${task.title}"`);
                    } else {
                        logChange('updated', 'task', task.id, task.title, Object.keys(updates).join(', '));
                    }

                    addAction(`✓ Updated task "${task.title}"`);
                    return `Updated task "${task.title}"${args.newTitle ? ` to "${args.newTitle}"` : ''}. You can still refer to it as "the task".`;
                }

                case 'completeTask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    if (!task.completed) {
                        currentAppState.toggleTaskComplete(task.id);
                        memory.lastMentionedTask = task.id;
                        logChange('completed', 'task', task.id, task.title);
                        addAction(`✓ Completed "${task.title}"`);
                        return `Completed task "${task.title}"`;
                    }
                    return `Task "${task.title}" was already completed`;
                }

                case 'deleteTask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    currentAppState.softDeleteTask(task.id);
                    logChange('deleted (to trash)', 'task', task.id, task.title);
                    addAction(`✓ Moved "${task.title}" to trash`);
                    return `Moved task "${task.title}" to trash. Say "permanently delete" to remove forever, or "restore" to bring it back.`;
                }

                case 'permanentlyDeleteTask': {
                    let task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) {
                        task = fuzzyFindTask(currentAppState.trashedTasks, args.taskTitle, memory);
                    }
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    currentAppState.permanentlyDeleteTask(task.id);
                    memory.tasks.delete(task.title);
                    logChange('permanently deleted', 'task', task.id, task.title);
                    addAction(`✓ Permanently deleted "${task.title}"`);
                    return `Permanently deleted task "${task.title}"`;
                }

                case 'restoreTask': {
                    const task = fuzzyFindTask(currentAppState.trashedTasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}" in trash`;

                    currentAppState.restoreTask(task.id);
                    memory.lastMentionedTask = task.id;
                    logChange('restored', 'task', task.id, task.title);
                    addAction(`✓ Restored "${task.title}"`);
                    return `Restored task "${task.title}" from trash`;
                }

                // ============ SUBTASKS ============
                case 'addSubtask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    currentAppState.addSubtask(task.id, args.subtaskTitle);
                    memory.lastMentionedTask = task.id;

                    // Track the subtask
                    setTimeout(() => {
                        const freshState = appStateRef.current;
                        const freshTask = freshState.tasks.find(t => t.id === task.id);
                        const newSubtask = freshTask?.subtasks?.find(s => s.title === args.subtaskTitle);
                        if (newSubtask) {
                            memory.lastMentionedSubtask = { taskId: task.id, subtaskId: newSubtask.id, title: args.subtaskTitle };
                        }
                    }, 100);

                    logChange('added', 'subtask', task.id, args.subtaskTitle, `to task "${task.title}"`);
                    addAction(`✓ Added subtask "${args.subtaskTitle}"`);
                    return `Added subtask "${args.subtaskTitle}" to task "${task.title}". You can refer to it as "the subtask".`;
                }

                case 'completeSubtask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    const subtask = fuzzyFindSubtask(task, args.subtaskTitle, memory);
                    if (!subtask) return `Could not find subtask "${args.subtaskTitle}" in task "${task.title}"`;

                    if (subtask.completed) {
                        return `Subtask "${subtask.title}" is already completed`;
                    }

                    currentAppState.toggleSubtaskComplete(task.id, subtask.id);
                    memory.lastMentionedTask = task.id;
                    memory.lastMentionedSubtask = { taskId: task.id, subtaskId: subtask.id, title: subtask.title };
                    logChange('completed', 'subtask', subtask.id, subtask.title, `in task "${task.title}"`);
                    addAction(`✓ Completed subtask "${subtask.title}"`);
                    return `Completed subtask "${subtask.title}" in task "${task.title}"`;
                }

                case 'listSubtasks': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    memory.lastMentionedTask = task.id;

                    if (!task.subtasks || task.subtasks.length === 0) {
                        return `Task "${task.title}" has no subtasks`;
                    }

                    const subtaskList = task.subtasks.map(s =>
                        `"${s.title}" [${s.completed ? 'done' : 'pending'}]`
                    ).join('; ');

                    return `Subtasks for "${task.title}": ${subtaskList}`;
                }

                case 'updateSubtask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    const subtask = fuzzyFindSubtask(task, args.subtaskTitle, memory);
                    if (!subtask) return `Could not find subtask "${args.subtaskTitle}" in task "${task.title}"`;

                    currentAppState.updateSubtask(task.id, subtask.id, { title: args.newTitle });
                    memory.lastMentionedTask = task.id;
                    memory.lastMentionedSubtask = { taskId: task.id, subtaskId: subtask.id, title: args.newTitle };
                    logChange('renamed', 'subtask', subtask.id, args.newTitle, `from "${subtask.title}"`);
                    addAction(`✓ Renamed subtask to "${args.newTitle}"`);
                    return `Renamed subtask from "${subtask.title}" to "${args.newTitle}"`;
                }

                case 'deleteSubtask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    const subtask = fuzzyFindSubtask(task, args.subtaskTitle, memory);
                    if (!subtask) return `Could not find subtask "${args.subtaskTitle}" in task "${task.title}"`;

                    currentAppState.deleteSubtask(task.id, subtask.id);
                    memory.lastMentionedTask = task.id;
                    logChange('deleted', 'subtask', subtask.id, subtask.title, `from task "${task.title}"`);
                    addAction(`✓ Deleted subtask "${subtask.title}"`);
                    return `Deleted subtask "${subtask.title}" from task "${task.title}"`;
                }

                case 'uncompleteSubtask': {
                    const task = fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory);
                    if (!task) return `Could not find task "${args.taskTitle}"`;

                    const subtask = fuzzyFindSubtask(task, args.subtaskTitle, memory);
                    if (!subtask) return `Could not find subtask "${args.subtaskTitle}" in task "${task.title}"`;

                    if (!subtask.completed) {
                        return `Subtask "${subtask.title}" is already incomplete`;
                    }

                    currentAppState.toggleSubtaskComplete(task.id, subtask.id);
                    memory.lastMentionedTask = task.id;
                    memory.lastMentionedSubtask = { taskId: task.id, subtaskId: subtask.id, title: subtask.title };
                    logChange('uncompleted', 'subtask', subtask.id, subtask.title);
                    addAction(`✓ Marked subtask "${subtask.title}" as incomplete`);
                    return `Marked subtask "${subtask.title}" as incomplete`;
                }

                // ============ PROJECTS ============
                case 'createProject': {
                    currentAppState.addProject({
                        name: args.name,
                        color: '#2A7A85',
                        bgColor: '#D9EEF2',
                        icon: args.icon || '📁',
                    });

                    const tempId = `temp_${Date.now()}`;
                    memory.projects.set(args.name, { id: tempId, name: args.name, createdAt: Date.now() });
                    memory.lastMentionedProject = tempId;

                    setTimeout(() => {
                        const freshState = appStateRef.current;
                        const proj = freshState.projects.find(p => p.name === args.name);
                        if (proj) {
                            memory.projects.set(args.name, { id: proj.id, name: args.name, createdAt: Date.now() });
                            memory.lastMentionedProject = proj.id;
                        }
                    }, 100);

                    logChange('created', 'project', tempId, args.name);
                    addAction(`✓ Created project "${args.name}"`);
                    return `Created project "${args.name}". You can refer to it as "the project".`;
                }

                case 'listProjects': {
                    if (currentAppState.projects.length === 0) return 'No projects';
                    return `Projects: ${currentAppState.projects.map(p => {
                        const taskCount = currentAppState.tasks.filter(t => t.projectId === p.id && !t.completed).length;
                        return `"${p.name}" (${taskCount} tasks)`;
                    }).join('; ')}`;
                }

                case 'updateProject': {
                    const project = fuzzyFindProject(currentAppState.projects, args.projectName, memory);
                    if (!project) return `Could not find project "${args.projectName}"`;

                    const updates: any = {};
                    if (args.newName) updates.name = args.newName;
                    if (args.icon) updates.icon = args.icon;

                    currentAppState.updateProject(project.id, updates);
                    memory.lastMentionedProject = project.id;

                    if (args.newName) {
                        memory.projects.delete(project.name);
                        memory.projects.set(args.newName, { id: project.id, name: args.newName, createdAt: Date.now() });
                        logChange('renamed', 'project', project.id, args.newName, `from "${project.name}"`);
                    } else {
                        logChange('updated', 'project', project.id, project.name);
                    }

                    addAction(`✓ Updated project "${project.name}"`);
                    return `Updated project "${project.name}"${args.newName ? ` to "${args.newName}"` : ''}`;
                }

                case 'deleteProject': {
                    const project = fuzzyFindProject(currentAppState.projects, args.projectName, memory);
                    if (!project) return `Could not find project "${args.projectName}"`;

                    const handleTasks = args.handleTasks === 'delete' ? 'delete' : 'unassign';
                    const taskCount = currentAppState.tasks.filter(t => t.projectId === project.id).length;

                    currentAppState.softDeleteProject(project.id, handleTasks);
                    logChange('archived', 'project', project.id, project.name, taskCount > 0 ? `${taskCount} tasks ${handleTasks === 'delete' ? 'deleted' : 'unassigned'}` : undefined);
                    addAction(`✓ Archived project "${project.name}"`);

                    let response = `Archived project "${project.name}"`;
                    if (taskCount > 0) {
                        response += handleTasks === 'delete'
                            ? `. ${taskCount} tasks moved to trash.`
                            : `. ${taskCount} tasks kept but unassigned.`;
                    }
                    return response + ' Say "restore project" to bring it back.';
                }

                case 'permanentlyDeleteProject': {
                    let project = fuzzyFindProject(currentAppState.projects, args.projectName, memory);
                    if (!project) {
                        project = fuzzyFindProject(currentAppState.trashedProjects, args.projectName, memory);
                    }
                    if (!project) return `Could not find project "${args.projectName}"`;

                    currentAppState.permanentlyDeleteProject(project.id);
                    memory.projects.delete(project.name);
                    logChange('permanently deleted', 'project', project.id, project.name);
                    addAction(`✓ Permanently deleted project "${project.name}"`);
                    return `Permanently deleted project "${project.name}"`;
                }

                case 'restoreProject': {
                    const project = fuzzyFindProject(currentAppState.trashedProjects, args.projectName, memory);
                    if (!project) return `Could not find project "${args.projectName}" in trash`;

                    currentAppState.restoreProject(project.id);
                    memory.lastMentionedProject = project.id;
                    logChange('restored', 'project', project.id, project.name);
                    addAction(`✓ Restored project "${project.name}"`);
                    return `Restored project "${project.name}" from trash`;
                }

                // ============ EVENTS ============
                case 'createEvent': {
                    await currentAppState.addEvent({
                        title: args.title,
                        date: args.date,
                        timeStart: args.timeStart,
                        timeEnd: args.timeEnd,
                        type: 'meeting',
                        color: '#8B9FD8',
                        location: args.location,
                    });

                    const tempId = `temp_${Date.now()}`;
                    memory.events.set(args.title, { id: tempId, title: args.title, createdAt: Date.now() });
                    memory.lastMentionedEvent = tempId;

                    setTimeout(() => {
                        const freshState = appStateRef.current;
                        const evt = freshState.events.find(e => e.title === args.title);
                        if (evt) {
                            memory.events.set(args.title, { id: evt.id, title: args.title, createdAt: Date.now() });
                            memory.lastMentionedEvent = evt.id;
                        }
                    }, 100);

                    logChange('created', 'event', tempId, args.title, `${args.date} ${args.timeStart}-${args.timeEnd}`);
                    addAction(`✓ Created event "${args.title}"`);
                    return `Created event "${args.title}" on ${args.date} at ${args.timeStart}-${args.timeEnd}. You can refer to it as "the event".`;
                }

                case 'listEvents': {
                    let events = currentAppState.events.filter(e => e.date && e.date >= today);
                    if (args.date) events = events.filter(e => e.date === args.date);

                    if (events.length === 0) return 'No upcoming events';
                    return `Events: ${events.slice(0, 5).map(e =>
                        `"${e.title}" on ${e.date} at ${e.timeStart}-${e.timeEnd}${e.location ? ` @ ${e.location}` : ''}`
                    ).join('; ')}`;
                }

                case 'updateEvent': {
                    const event = fuzzyFindEvent(currentAppState.events, args.eventTitle, memory);
                    if (!event) return `Could not find event "${args.eventTitle}"`;

                    const updates: any = {};
                    if (args.newTitle) updates.title = args.newTitle;
                    if (args.date) updates.date = args.date;
                    if (args.timeStart) updates.timeStart = args.timeStart;
                    if (args.timeEnd) updates.timeEnd = args.timeEnd;
                    if (args.location) updates.location = args.location;

                    await currentAppState.updateEvent(event.id, updates);
                    memory.lastMentionedEvent = event.id;

                    if (args.newTitle) {
                        memory.events.delete(event.title);
                        memory.events.set(args.newTitle, { id: event.id, title: args.newTitle, createdAt: Date.now() });
                        logChange('renamed', 'event', event.id, args.newTitle, `from "${event.title}"`);
                    } else {
                        logChange('updated', 'event', event.id, event.title);
                    }

                    addAction(`✓ Updated event "${event.title}"`);
                    return `Updated event "${event.title}"`;
                }

                case 'deleteEvent': {
                    const event = fuzzyFindEvent(currentAppState.events, args.eventTitle, memory);
                    if (!event) return `Could not find event "${args.eventTitle}"`;

                    currentAppState.softDeleteEvent(event.id);
                    logChange('deleted (to trash)', 'event', event.id, event.title);
                    addAction(`✓ Moved "${event.title}" to trash`);
                    return `Moved event "${event.title}" to trash. Say "permanently delete" to remove forever.`;
                }

                case 'permanentlyDeleteEvent': {
                    let event = fuzzyFindEvent(currentAppState.events, args.eventTitle, memory);
                    if (!event) {
                        event = fuzzyFindEvent(currentAppState.trashedEvents, args.eventTitle, memory);
                    }
                    if (!event) return `Could not find event "${args.eventTitle}"`;

                    await currentAppState.permanentlyDeleteEvent(event.id);
                    memory.events.delete(event.title);
                    logChange('permanently deleted', 'event', event.id, event.title);
                    addAction(`✓ Permanently deleted "${event.title}"`);
                    return `Permanently deleted event "${event.title}"`;
                }

                case 'restoreEvent': {
                    const event = fuzzyFindEvent(currentAppState.trashedEvents, args.eventTitle, memory);
                    if (!event) return `Could not find event "${args.eventTitle}" in trash`;

                    currentAppState.restoreEvent(event.id);
                    memory.lastMentionedEvent = event.id;
                    logChange('restored', 'event', event.id, event.title);
                    addAction(`✓ Restored "${event.title}"`);
                    return `Restored event "${event.title}" from trash`;
                }

                // ============ SESSION CONTEXT ============
                case 'getSessionContext': {
                    const recentChanges = memory.changeLog.slice(-10).map(c =>
                        `${c.action} ${c.itemType} "${c.itemTitle}"${c.details ? ` (${c.details})` : ''}`
                    ).join('; ');

                    const recentTasks = Array.from(memory.tasks.values()).slice(-5);
                    const recentProjects = Array.from(memory.projects.values()).slice(-3);

                    let context = `Session changes: ${recentChanges || 'none'}\n`;
                    context += `Created items: Tasks: ${recentTasks.map(t => `"${t.title}"`).join(', ') || 'none'}; `;
                    context += `Projects: ${recentProjects.map(p => `"${p.name}"`).join(', ') || 'none'}\n`;
                    context += `Last mentioned: Task ID ${memory.lastMentionedTask || 'none'}, Project ID ${memory.lastMentionedProject || 'none'}`;

                    return context;
                }

                // ============ NAVIGATION ============
                case 'startFocusMode': {
                    let task = args.taskTitle
                        ? fuzzyFindTask(currentAppState.tasks, args.taskTitle, memory)
                        : null;

                    // If no task specified, use first incomplete task or last mentioned
                    if (!task) {
                        if (memory.lastMentionedTask) {
                            task = currentAppState.tasks.find(t => t.id === memory.lastMentionedTask && !t.completed);
                        }
                        if (!task) {
                            task = currentAppState.tasks.find(t => !t.completed);
                        }
                    }

                    if (!task) {
                        return 'No incomplete tasks found to focus on. Create a task first!';
                    }

                    // Navigate to focus mode with task
                    const url = `/app/focus?taskId=${encodeURIComponent(task.id)}`;

                    // Use window.location for navigation since we're not in router context
                    setTimeout(() => {
                        window.location.href = url;
                    }, 500); // Small delay so user hears response

                    memory.lastMentionedTask = task.id;
                    logChange('started focus mode', 'task', task.id, task.title);
                    addAction(`🎯 Starting focus mode on "${task.title}"`);
                    return `Starting focus mode on "${task.title}". Let's get it done!`;
                }

                case 'navigateTo': {
                    const pageRoutes: Record<string, string> = {
                        analytics: '/app/analytics',
                        briefing: '/app/briefing',
                        debrief: '/app/debrief',
                        schedule: '/app/schedule',
                        priorities: '/app/priorities',
                        calendar: '/app/calendar',
                        inbox: '/app/inbox',
                        profile: '/app/profile',
                    };

                    const route = pageRoutes[args.page];
                    if (!route) {
                        return `I don't know how to navigate to "${args.page}". Try: analytics, briefing, debrief, schedule, priorities, calendar, inbox, or profile.`;
                    }

                    setTimeout(() => {
                        window.location.href = route;
                    }, 500);

                    const pageNames: Record<string, string> = {
                        analytics: 'Analytics Dashboard',
                        briefing: 'Morning Briefing',
                        debrief: 'Evening Debrief',
                        schedule: 'Schedule Optimizer',
                        priorities: 'Priority Board',
                        calendar: 'Calendar',
                        inbox: 'Inbox',
                        profile: 'Profile',
                    };

                    addAction(`🧭 Navigating to ${pageNames[args.page]}`);
                    return `Taking you to ${pageNames[args.page]}!`;
                }

                case 'generateMorningBriefing': {
                    const today = new Date().toISOString().split('T')[0];
                    const todayTasks = currentAppState.tasks.filter(t =>
                        !t.deletedAt &&
                        (t.dueDate === today || (!t.dueDate && !t.completed))
                    );
                    const incompleteTasks = todayTasks.filter(t => !t.completed);
                    const highPriority = incompleteTasks.filter(t => t.priority === 'high');
                    const todayEvents = currentAppState.events.filter(e =>
                        e.date === today && !e.deletedAt
                    );

                    let briefing = `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}! Here's your briefing. `;

                    if (highPriority.length > 0) {
                        briefing += `You have ${highPriority.length} high-priority task${highPriority.length > 1 ? 's' : ''} today: ${highPriority.slice(0, 3).map(t => t.title).join(', ')}. `;
                    } else if (incompleteTasks.length > 0) {
                        briefing += `You have ${incompleteTasks.length} task${incompleteTasks.length > 1 ? 's' : ''} for today. `;
                    } else {
                        briefing += `Your task list is clear! `;
                    }

                    if (todayEvents.length > 0) {
                        briefing += `On your calendar: ${todayEvents.length} event${todayEvents.length > 1 ? 's' : ''}, starting with ${todayEvents[0].title} at ${todayEvents[0].timeStart}. `;
                    }

                    if (incompleteTasks.length > 5) {
                        briefing += `Pro tip: Consider using the Schedule Optimizer to spread out your workload.`;
                    }

                    addAction('📋 Generated morning briefing');
                    return briefing;
                }

                // ============ EVENING DEBRIEF FUNCTIONS ============
                case 'startEveningDebrief': {
                    // Reset the debrief session in localStorage
                    const newSession: DebriefSession = {
                        date: new Date().toISOString().split('T')[0],
                        rating: 0,
                        wins: [],
                        improvements: [],
                        gratitude: [],
                        journal: '',
                        isActive: true,
                    };
                    setDebriefSession(newSession);
                    console.log('[VoiceAssistant] Started debrief session:', newSession);

                    addAction('🌙 Starting Evening Debrief');
                    return `Let's do your evening debrief! First, how would you rate your day on a scale of 1 to 5 stars? 1 being tough, 5 being amazing.`;
                }

                case 'setDayRating': {
                    const session = getDebriefSession();
                    const rating = Math.min(5, Math.max(1, Math.round(args.rating)));
                    session.rating = rating;
                    setDebriefSession(session);
                    console.log('[VoiceAssistant] Set day rating:', rating);

                    const ratingMessages: Record<number, string> = {
                        1: `Got it, a 1-star day. Tomorrow will be better! `,
                        2: `Okay, a 2-star day. You still showed up, that counts! `,
                        3: `A 3-star day, solid middle ground. `,
                        4: `Nice! A 4-star day, that's great! `,
                        5: `Amazing! A 5-star day, you crushed it! `,
                    };

                    addAction(`⭐ Day rating: ${rating}/5`);
                    return `${ratingMessages[rating]} Now, what were your wins today? Tell me what you accomplished or what went well.`;
                }

                case 'addDebriefWin': {
                    if (args.win) {
                        const session = getDebriefSession();
                        session.wins.push(args.win);
                        setDebriefSession(session);
                        console.log('[VoiceAssistant] Added win:', args.win);
                        addAction(`✅ Win: ${args.win}`);

                        if (session.wins.length >= 3) {
                            return `Great wins! I've captured: ${session.wins.join(', ')}. Now, what's one thing you'd do differently tomorrow?`;
                        }
                        return `Got it! "${args.win}" - that's a solid win. Any other wins, or should we move on?`;
                    }
                    return 'What win would you like to add?';
                }

                case 'addDebriefImprovement': {
                    if (args.improvement) {
                        const session = getDebriefSession();
                        session.improvements.push(args.improvement);
                        setDebriefSession(session);
                        console.log('[VoiceAssistant] Added improvement:', args.improvement);
                        addAction(`🎯 Tomorrow: ${args.improvement}`);

                        if (session.improvements.length >= 2) {
                            return `Great intentions for tomorrow! Now, what are you grateful for today? Give me up to 3 things.`;
                        }
                        return `Got it, you'll "${args.improvement}" tomorrow. Anything else, or ready for gratitude?`;
                    }
                    return 'What would you do differently?';
                }

                case 'addDebriefGratitude': {
                    if (args.gratitude) {
                        const session = getDebriefSession();
                        if (session.gratitude.length < 3) {
                            session.gratitude.push(args.gratitude);
                            setDebriefSession(session);
                            console.log('[VoiceAssistant] Added gratitude:', args.gratitude);
                            addAction(`🙏 Grateful: ${args.gratitude}`);

                            const count = session.gratitude.length;
                            if (count >= 3) {
                                return `Beautiful! Three things to be grateful for. Finally, any thoughts you want to capture in your journal? Just share freely.`;
                            }
                            return `${count} of 3 gratitude items. What else are you grateful for?`;
                        }
                    }
                    return 'What are you grateful for?';
                }

                case 'setDebriefJournal': {
                    if (args.journal) {
                        const session = getDebriefSession();
                        session.journal = args.journal;
                        setDebriefSession(session);
                        console.log('[VoiceAssistant] Set journal:', args.journal);
                        addAction('📝 Journal captured');
                        return `Got it, I've captured your thoughts. Ready to save your debrief?`;
                    }
                    return 'What thoughts would you like to journal?';
                }

                case 'saveEveningDebrief': {
                    const debrief = getDebriefSession();

                    if (!debrief.isActive) {
                        return `No active debrief to save. Say "let's do my evening debrief" to start one.`;
                    }

                    // Save to localStorage reflections
                    const reflection = {
                        date: debrief.date,
                        rating: debrief.rating,
                        wins: debrief.wins,
                        improvements: debrief.improvements,
                        journal: debrief.journal,
                        gratitude: debrief.gratitude,
                    };

                    console.log('[VoiceAssistant] Saving debrief:', reflection);

                    const reflections = JSON.parse(localStorage.getItem('reflections') || '{}');
                    reflections[debrief.date] = reflection;
                    localStorage.setItem('reflections', JSON.stringify(reflections));

                    console.log('[VoiceAssistant] Saved to localStorage, total reflections:', Object.keys(reflections).length);

                    // Dispatch storage event to trigger refresh in other components
                    window.dispatchEvent(new StorageEvent('storage', {
                        key: 'reflections',
                        newValue: JSON.stringify(reflections),
                    }));

                    // SEMANTIC MEMORY: Extract key facts and save to vector store
                    if (user?.id) {
                        try {
                            const savedFacts = await saveDebriefFacts(user.id, {
                                date: debrief.date,
                                rating: debrief.rating,
                                wins: debrief.wins,
                                improvements: debrief.improvements,
                                gratitude: debrief.gratitude,
                                journal: debrief.journal,
                            });
                            console.log(`[VoiceAssistant] Extracted and saved ${savedFacts.length} key facts to semantic memory`);
                            if (savedFacts.length > 0) {
                                addAction(`🧠 ${savedFacts.length} memories stored`);
                            }
                        } catch (e) {
                            console.error('[VoiceAssistant] Failed to save key facts:', e);
                        }
                    }

                    // Clear the active session
                    localStorage.removeItem(DEBRIEF_SESSION_KEY);

                    addAction('💾 Evening debrief saved!');

                    const summary = debrief.rating >= 4
                        ? `What a great day! `
                        : debrief.rating <= 2
                            ? `Tomorrow's a fresh start. `
                            : '';

                    return `${summary}Your evening debrief has been saved! You logged ${debrief.wins.length} wins, ${debrief.gratitude.length} gratitude items, and your reflections for the day. Sweet dreams!`;
                }

                // ============ REBALANCING ============
                case 'suggestRebalance': {
                    const today = new Date().toISOString().split('T')[0];

                    // Find overdue high-priority tasks
                    const overdueTasks = getOverdueHighPriorityTasks(currentAppState.tasks, today);

                    // Find incomplete habits due today
                    const habitGaps = getHabitGaps(currentAppState.habits, today);

                    // Find available time slots
                    const availableSlots = findAvailableSlots(
                        currentAppState.tasks,
                        currentAppState.events,
                        today
                    );

                    // Build response
                    let response = '';

                    if (overdueTasks.length === 0 && habitGaps.length === 0) {
                        response = "You're all caught up! No overdue high-priority tasks and all your habits are on track for today.";
                    } else {
                        if (overdueTasks.length > 0) {
                            response += `I found ${overdueTasks.length} overdue high-priority task${overdueTasks.length > 1 ? 's' : ''}: `;
                            response += overdueTasks.slice(0, 3).map(t => `"${t.title}" (due ${t.dueDate})`).join(', ') + '. ';
                        }

                        if (habitGaps.length > 0) {
                            response += `Habit${habitGaps.length > 1 ? 's' : ''} not done today: `;
                            response += habitGaps.slice(0, 3).map(h => `${h.icon} ${h.title}`).join(', ') + '. ';
                        }

                        if (availableSlots.length > 0) {
                            const bestSlot = availableSlots.reduce((a, b) =>
                                a.durationMinutes > b.durationMinutes ? a : b
                            );
                            response += `Best available slot: ${bestSlot.start} to ${bestSlot.end} (${bestSlot.durationMinutes} minutes). `;
                            response += 'Want me to reschedule something into that window?';
                        } else {
                            response += "Your schedule is pretty packed today. Want me to push something less important to tomorrow?";
                        }
                    }

                    addAction('📊 Analyzed schedule for rebalancing');
                    return response;
                }

                // ============ IDENTITY OBSERVER ============
                case 'updateUserIdentity': {
                    const addRoles = args.addRoles as string[] | undefined;
                    const addVipEntities = args.addVipEntities as string[] | undefined;
                    const updatePrimaryGoal = args.updatePrimaryGoal as string | undefined;

                    const changes: string[] = [];

                    // Add new roles without duplicates
                    if (addRoles && addRoles.length > 0) {
                        addRolesToIdentity(addRoles);
                        changes.push(`Added role${addRoles.length > 1 ? 's' : ''}: ${addRoles.join(', ')}`);
                    }

                    // Add new VIP entities without duplicates
                    if (addVipEntities && addVipEntities.length > 0) {
                        addVipsToIdentity(addVipEntities);
                        changes.push(`Added VIP${addVipEntities.length > 1 ? 's' : ''}: ${addVipEntities.join(', ')}`);
                    }

                    // Update primary goal if provided
                    if (updatePrimaryGoal) {
                        saveUserIdentity({ primaryGoal: updatePrimaryGoal });
                        changes.push(`Updated North Star to: "${updatePrimaryGoal}"`);
                    }

                    if (changes.length === 0) {
                        return 'No identity updates provided.';
                    }

                    addAction(`🆔 Updated identity: ${changes.join('; ')}`);
                    return `Identity updated: ${changes.join('. ')}.`;
                }

                // ============ TASK DECOMPOSITION ============
                case 'decomposeTask': {
                    const newProjectTitle = args.newProjectTitle as string;
                    const subTasks = args.subTasks as Array<{
                        title: string;
                        estimatedDurationMinutes?: number;
                        priority?: 'high' | 'normal' | 'low';
                    }>;

                    if (!newProjectTitle || !subTasks || subTasks.length === 0) {
                        return 'Please provide a project title and at least one sub-task.';
                    }

                    // 1. Create the project first
                    currentAppState.addProject({
                        name: newProjectTitle,
                        icon: '📋',
                        color: '#2A7A85',
                        bgColor: '#D9EEF2',
                    });

                    // Wait briefly for project to be created, then get its ID
                    const projectId = `proj_temp_${Date.now()}`;
                    memory.projects.set(newProjectTitle, { id: projectId, name: newProjectTitle, createdAt: Date.now() });
                    memory.lastMentionedProject = projectId;

                    // 2. Create tasks linked to project
                    const today = new Date().toISOString().split('T')[0];
                    const createdTasks: { title: string; priority: string }[] = [];

                    // Get project ID after creation (use setTimeout pattern from createProject)
                    setTimeout(() => {
                        const freshState = appStateRef.current;
                        const proj = freshState.projects.find(p => p.name === newProjectTitle);
                        if (proj) {
                            memory.projects.set(newProjectTitle, { id: proj.id, name: newProjectTitle, createdAt: Date.now() });
                            memory.lastMentionedProject = proj.id;

                            // Now create tasks with the real project ID
                            for (const sub of subTasks) {
                                // Map priority: tool uses 'low' but app uses 'normal' as lowest
                                const rawPriority = sub.priority || 'normal';
                                const priority = rawPriority === 'low' ? 'normal' : rawPriority;
                                currentAppState.addTask({
                                    title: sub.title,
                                    completed: false,
                                    projectId: proj.id,
                                    priority: priority as 'high' | 'medium' | 'normal',
                                    dueDate: rawPriority === 'high' ? today : undefined,
                                    description: sub.estimatedDurationMinutes
                                        ? `Estimated: ~${sub.estimatedDurationMinutes} min`
                                        : undefined,
                                    context: 'Decomposed',
                                    contextColor: '#7C3AED',
                                    contextBgColor: '#EDE9FE',
                                    subtasks: [],
                                });
                            }
                        }
                    }, 150);

                    // Record what was created
                    for (const sub of subTasks) {
                        createdTasks.push({ title: sub.title, priority: sub.priority || 'normal' });
                    }

                    // 3. Return summary
                    const firstTask = createdTasks[0]?.title || 'the first step';
                    logChange('created', 'project', projectId, newProjectTitle, `with ${createdTasks.length} decomposed tasks`);
                    addAction(`📋 Decomposed "${newProjectTitle}" into ${createdTasks.length} tasks`);

                    return `Created project "${newProjectTitle}" with ${createdTasks.length} tasks. The first step is "${firstTask}". Should we schedule that one for today?`;
                }

                default:
                    return `Unknown function: ${name}`;
            }
        } catch (error) {
            console.error('[VoiceAssistant] Function error:', error);
            return `Error executing ${name}: ${error}`;
        }
    }, [addAction, logChange]);

    // Cache for semantic context (fetched once per session start)
    const semanticContextRef = useRef<VectorMemoryEntry[]>([]);

    // Fetch semantic context based on current tasks/habits
    const fetchSemanticContext = useCallback(async (): Promise<VectorMemoryEntry[]> => {
        if (!user?.id) return [];

        try {
            const freshState = appStateRef.current;

            // Build a query from current incomplete tasks and habits
            const queryParts: string[] = [];

            // Add incomplete task titles
            freshState.tasks
                .filter(t => !t.completed && !t.deletedAt)
                .slice(0, 5)
                .forEach(t => queryParts.push(t.title));

            // Add habit titles
            freshState.habits
                .slice(0, 3)
                .forEach(h => queryParts.push(h.title));

            // Add context about what user might need help with
            if (freshState.tasks.some(t => t.priority === 'high' && !t.completed)) {
                queryParts.push('high priority urgent');
            }

            const queryText = queryParts.join(' ') || 'productivity goals achievements';

            console.log('[VoiceAssistant] Fetching semantic context for:', queryText.slice(0, 50));

            const memories = await searchSemanticMemories(user.id, queryText, 3);
            semanticContextRef.current = memories;

            console.log(`[VoiceAssistant] Loaded ${memories.length} relevant memories`);
            return memories;
        } catch (e) {
            console.error('[VoiceAssistant] Error fetching semantic context:', e);
            return [];
        }
    }, [user?.id]);

    // Context provider with fresh data
    const getContextProvider = useCallback(() => {
        return (): AIContext => {
            const now = new Date();
            const freshState = appStateRef.current;
            const dateFormatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: freshState.userTimezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
            const timeFormatter = new Intl.DateTimeFormat('en-US', {
                timeZone: freshState.userTimezone,
                hour: '2-digit',
                minute: '2-digit',
            });
            // Load recent reflections from localStorage
            let recentReflections: { date: string; rating: number; wins: string[]; improvements: string[]; journal: string; gratitude: string[]; }[] = [];
            try {
                const stored = localStorage.getItem('reflections');
                if (stored) {
                    const reflections = JSON.parse(stored) as Record<string, { date: string; rating: number; wins: string[]; improvements: string[]; journal: string; gratitude: string[]; }>;
                    recentReflections = Object.values(reflections)
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .slice(0, 7); // Last 7 reflections (about a week)
                }
            } catch (e) {
                console.error('[VoiceAssistant] Error loading reflections:', e);
            }

            return {
                projects: freshState.projects,
                tasks: freshState.tasks,
                events: freshState.events,
                inboxItems: freshState.inboxItems,
                currentDate: dateFormatter.format(now),
                currentTime: timeFormatter.format(now),
                timezone: freshState.userTimezone,

                personaPrompt: AI_PERSONAS[freshState.aiPersona].systemPrompt + (isFocusModeActiveRef.current
                    ? ' The user is currently in a deep work focus session. Be extremely concise. Do not engage in small talk. Only interrupt for emergencies. If they ask to capture a task, do it silently and confirm with one word.'
                    : ''),
                voiceStyle: AI_PERSONAS[freshState.aiPersona].voiceStyle,
                voice: AI_PERSONAS[freshState.aiPersona].voice,
                recentReflections,
                semanticContext: semanticContextRef.current, // Include cached semantic memories
                // Habits and Goals for context hydration
                habits: freshState.habits,
                goals: freshState.goals,
                // Calculate workload score for today
                workloadScore: calculateWorkloadScore({
                    tasks: freshState.tasks,
                    events: freshState.events,
                    currentDate: dateFormatter.format(now),
                }),
                // User identity for personalized context
                userIdentity: getUserIdentity(),
            };
        };
    }, []);


    // Initialize session
    const initSession = useCallback(async () => {
        if (sessionRef.current) return;

        // Reset session memory on new session
        sessionMemoryRef.current = {
            tasks: new Map(),
            projects: new Map(),
            events: new Map(),
            lastMentionedTask: null,
            lastMentionedProject: null,
            lastMentionedEvent: null,
            lastMentionedSubtask: null,
            changeLog: [],
        };

        setVolumeLevel(0);

        try {
            setState('connecting');
            setErrorMessage('');

            // Fetch semantic context before connecting (don't block on it)
            fetchSemanticContext().catch(e =>
                console.warn('[VoiceAssistant] Semantic context fetch failed:', e)
            );

            const session = new GeminiLiveSession();
            const player = new AudioPlayer();

            const contextProvider = getContextProvider();
            const initialContext = contextProvider();


            session.onAudioResponse((audioData) => {
                // Reset interrupt state so new audio can play
                player.resetInterrupt();
                player.play(audioData);
                setState('speaking');
            });

            session.onTextResponse((text) => {
                setChatHistory(prev => {
                    const last = prev[prev.length - 1];
                    if (last && last.role === 'assistant') {
                        return [...prev.slice(0, -1), { ...last, text: last.text + ' ' + text }];
                    }
                    return [...prev, { role: 'assistant', text, actions: [] }];
                });
            });

            session.onFunctionCall(executeFunction);

            session.onStateChange((connectionState, error) => {
                if (connectionState === 'connected') {
                    setState('ready');
                    reconnectAttemptedRef.current = false;
                }
                else if (connectionState === 'error') {
                    // Check for WebSocket code 1006 (Abnormal Closure)
                    if (error && error.code === 1006 && !reconnectAttemptedRef.current) {
                        console.log('[VoiceAssistant] Detected 1006 disconnect - attempting one reconnection');
                        reconnectAttemptedRef.current = true;

                        // Play "down-tone" to notify user
                        if (playerRef.current) {
                            playerRef.current.playTone(200, 'sine', 0.8);
                        }

                        setErrorMessage('Reconnecting...');

                        // Wait 1s and try to reconnect
                        setTimeout(() => {
                            session.connect(initialContext, contextProvider).catch(e => {
                                console.error('[VoiceAssistant] Reconnection failed:', e);
                                setState('error');
                                setErrorMessage('Connection failed');
                            });
                        }, 1000);
                        return;
                    }

                    setState('error');
                    setErrorMessage('Connection failed');
                }
                else if (connectionState === 'disconnected') {
                    // Treat unexpected disconnects as errors to trigger recovery or feedback
                    // unless we explicitly called disconnect (which would clear sessionRef earlier)

                    if (sessionRef.current && !reconnectAttemptedRef.current) {
                        console.log('[VoiceAssistant] Unexpected disconnect - attempting one reconnection');
                        reconnectAttemptedRef.current = true;

                        // Feedback
                        if (playerRef.current) {
                            playerRef.current.playTone(300, 'sine', 0.5);
                        }
                        setErrorMessage('Reconnecting...');

                        setTimeout(() => {
                            session.connect(initialContext, contextProvider).catch(e => {
                                console.error('[VoiceAssistant] Reconnection failed:', e);
                                setState('error');
                                setErrorMessage('Connection failed');
                            });
                        }, 1000);
                        return;
                    }

                    // If we already tried, or if it was intentional, just reset
                    if (state !== 'idle') {
                        setState('idle'); // Or 'error' if we want to be explicit
                    }
                }
            });

            player.setOnPlaybackEnd(() => setState('ready'));

            await session.connect(initialContext, contextProvider);

            sessionRef.current = session;
            playerRef.current = player;

            // Start Voice Activity Detection for hands-free interrupt
            // When user speaks while AI is talking, automatically interrupt
            const vad = new VoiceActivityDetector();
            try {
                await vad.start(
                    // onSpeechStart - User started speaking
                    async () => {
                        console.log('[VAD] Speech detected - checking interruption');

                        // Interrupt AI if speaking
                        if (playerRef.current?.isCurrentlyPlaying()) {
                            playerRef.current.interrupt();
                        }

                        // Automatically start capture if not already recording
                        if (!captureRef.current) {
                            const capture = new AudioCapture();
                            capture.start(
                                (audioData) => {
                                    sessionRef.current?.sendAudio(audioData);
                                },
                                (volume) => {
                                    setVolumeLevel(volume);
                                }
                            ).then(() => {
                                captureRef.current = capture;
                                setIsRecording(true);
                                setState('listening');
                                setChatHistory(prev => [...prev, { role: 'user', text: '🎤 Speaking...' }]);
                                // Pause VAD while actively recording
                                vad.pause();
                            }).catch(err => {
                                console.error('[VAD] Failed to start capture:', err);
                            });
                        }
                    },
                    // onSpeechEnd
                    () => {
                        console.log('[VAD] User speech ended');
                    }
                );
                vadRef.current = vad;
                console.log('[VoiceAssistant] Hands-free mode enabled');
            } catch (vadError) {
                console.warn('[VoiceAssistant] VAD not available:', vadError);
            }

        } catch (error) {
            console.error('Init failed:', error);
            setState('error');
            setErrorMessage(error instanceof Error ? error.message : 'Failed to connect');
        }
    }, [getContextProvider, executeFunction]);

    const open = useCallback(() => {
        setIsOpen(true);
        setIsMinimized(false);
        initSession();
    }, [initSession]);

    const close = useCallback(() => {
        setIsOpen(false);
        setIsMinimized(false);
        captureRef.current?.stop();
        captureRef.current = null;
        sessionRef.current?.disconnect();
        sessionRef.current = null;
        playerRef.current?.stop();
        playerRef.current = null;
        vadRef.current?.stop();
        vadRef.current = null;
        setState('idle');
        setIsRecording(false);
        setChatHistory([]);
    }, []);

    const minimize = useCallback(() => setIsMinimized(true), []);
    const expand = useCallback(() => setIsMinimized(false), []);

    const startListening = useCallback(async () => {
        if (state !== 'ready' && state !== 'speaking') return;

        try {
            // INTERRUPT: Stop AI audio immediately when user starts speaking
            if (playerRef.current) {
                playerRef.current.interrupt();
                // CRITICAL: Ensure player context is resumed while we have a user gesture
                await playerRef.current.resume();
            }

            // Pause VAD while manually recording
            vadRef.current?.pause();

            const capture = new AudioCapture();
            await capture.start(
                (audioData) => {
                    sessionRef.current?.sendAudio(audioData);
                },
                (volume) => {
                    setVolumeLevel(volume);
                }
            );

            captureRef.current = capture;
            setIsRecording(true);
            setState('listening');
            setChatHistory(prev => [...prev, { role: 'user', text: '🎤 Speaking...' }]);
        } catch (error) {
            console.error('Mic error:', error);
            setErrorMessage('Microphone access denied');
            setState('error');
        }
    }, [state]);

    const stopListening = useCallback(() => {
        captureRef.current?.stop();
        captureRef.current = null;
        setIsRecording(false);
        sessionRef.current?.endTurn();
        setState('processing');

        // Resume VAD for hands-free interrupt on next AI response
        vadRef.current?.resume();

        setChatHistory(prev => {
            const updated = [...prev];
            if (updated.length > 0 && updated[updated.length - 1].role === 'user') {
                updated[updated.length - 1] = { role: 'user', text: '🎤 Spoke to assistant' };
            }
            return updated;
        });
    }, []);

    useEffect(() => {
        return () => {
            captureRef.current?.stop();
            sessionRef.current?.disconnect();
            playerRef.current?.stop();
        };
    }, []);

    const value: VoiceAssistantContextType = {
        isOpen, isMinimized, state, chatHistory, errorMessage,
        open, close, minimize, expand, startListening, stopListening, isRecording,
        volumeLevel,
    };

    return (
        <VoiceAssistantContext.Provider value={value}>
            {children}
        </VoiceAssistantContext.Provider>
    );
}
